import { useEffect, useState } from "react";
import api, { apiErr } from "@/lib/api";
import { fmtDateTime } from "@/lib/format";
import { APP } from "@/constants/testIds";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { History, RefreshCw } from "lucide-react";
import { toast } from "sonner";

// Keep in sync with the entity_type values written by core.audit() across the backend routes.
const TYPES = [
  "all", "work_order", "score_period", "evidence_pack", "contract", "site", "zone",
  "asset", "entity", "defect", "intervention", "indicator", "value_calc",
  "survival_register", "tender_pack", "wizard", "user", "integration",
];

const pretty = (s) => (s || "").replace(/_/g, " ");

export default function AuditPage() {
  const [rows, setRows] = useState([]);
  const [type, setType] = useState("all");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  const load = (t) => {
    const q = t && t !== "all" ? `?entity_type=${t}` : "";
    setLoading(true);
    setError(false);
    api.get(`/audit${q}`)
      .then((r) => setRows(Array.isArray(r.data) ? r.data : []))
      .catch((e) => { setError(true); setRows([]); toast.error(apiErr(e, "Couldn't load the audit trail.")); })
      .finally(() => setLoading(false));
  };
  useEffect(() => { load(type); }, [type]);

  return (
    <div className="space-y-5" data-testid={APP.navAudit + "-page"}>
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="flex items-center gap-2 font-heading text-2xl font-bold text-forest-dark">
            <History className="h-6 w-6 text-forest-mid" /> Audit Trail
          </h1>
          <p className="text-sm text-muted-foreground">An immutable record of key changes — so nothing is silently altered.</p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" className="h-9" onClick={() => load(type)} disabled={loading} data-testid="audit-refresh">
            <RefreshCw className={`mr-1.5 h-4 w-4 ${loading ? "animate-spin" : ""}`} /> Refresh
          </Button>
          <Select value={type} onValueChange={setType}>
            <SelectTrigger className="w-[200px]" data-testid="audit-type-filter"><SelectValue /></SelectTrigger>
            <SelectContent>{TYPES.map((t) => <SelectItem key={t} value={t} className="capitalize">{pretty(t)}</SelectItem>)}</SelectContent>
          </Select>
        </div>
      </div>

      <Card className="border-sage-light">
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>When</TableHead><TableHead>Record</TableHead><TableHead>Action</TableHead>
                <TableHead>Details</TableHead><TableHead>By</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading && <TableRow><TableCell colSpan={5} className="py-8 text-center text-sm text-muted-foreground">Loading audit entries…</TableCell></TableRow>}
              {!loading && error && <TableRow><TableCell colSpan={5} className="py-8 text-center text-sm text-rose-600">Couldn't load the audit trail. Tap Refresh to try again.</TableCell></TableRow>}
              {!loading && !error && rows.length === 0 && <TableRow><TableCell colSpan={5} className="py-8 text-center text-sm text-muted-foreground">No audit entries for this filter.</TableCell></TableRow>}
              {!loading && !error && rows.map((a) => (
                <TableRow key={a.id}>
                  <TableCell className="whitespace-nowrap text-xs text-muted-foreground">{fmtDateTime(a.timestamp)}</TableCell>
                  <TableCell className="text-sm font-medium capitalize">{pretty(a.entity_type)}</TableCell>
                  <TableCell className="text-sm capitalize text-muted-foreground">{pretty(a.action)}</TableCell>
                  <TableCell className="max-w-xs truncate font-mono text-xs text-muted-foreground">{Object.keys(a.changes || {}).length ? JSON.stringify(a.changes) : "—"}</TableCell>
                  <TableCell className="text-sm">{a.user_name} <span className="text-xs text-muted-foreground">({pretty(a.user_role)})</span></TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      </Card>
    </div>
  );
}
